import { ReactNode } from 'react'

import { useMe } from '@/providers'
import { NotAuthorized } from '@views/error-pages/ErrorPage.stories'

export enum Permissions {
	BasketEdit = 'MARKET_BASKET_EDIT',
	ManageUsers = 'BI_MANAGE_USERS',
	BasketTest = 'MARKET_BASKET_TEST',
	ViewSource = 'BASKET_SOURCE_VIEW',
	BiDashboardView = 'BI_DASHBOARD_VIEW',
	FeatureFlagsUpdate = 'FEATURE_FLAGS_UPDATE',
	AppSettingsUpdate = 'APP_SETTINGS_UPDATE',
	CanImpersonateRole = 'CAN_IMPERSONATE_ROLE',
	WonderlandView = 'WONDERLAND_VIEW',
	ManageRoles = 'MANAGE_ROLES',
	UseMagicBaskets = 'USE_MAGIC_BASKETS',
	MarketBasketLayoutAdmin = 'MARKET_BASKET_LAYOUT_ADMIN',
	MarketBasketSettings = 'MARKET_BASKET_SYSTEM_SETTINGS',
	SwitchTracking = 'SWITCH_TRACKING',
	TaskServerView = 'TASK_SERVER_VIEW',
}

type Props = {
	showWhenHasPermission?: string[]
	showWhenMissingPermission?: string[]
	disableNotAuthorized?: boolean
	children: ReactNode
}

/**
 * Controls rendering of children based on the permissions.
 *
 * @example:
 *   <PermissionsGuard showWhenHasPermission={['bi-manage-users']}>
 *     <Users />
 *   </PermissionsGuard>
 *
 */
export default function PermissionsGuard({
	showWhenHasPermission = [],
	showWhenMissingPermission = [],
	disableNotAuthorized = false,
	children,
}: Props) {
	const { permissions: userPermissions } = useMe()

	const hasPermission = (permission) => !!userPermissions.find((p) => p === permission)

	if (showWhenHasPermission.length === 0 && showWhenMissingPermission.length === 0) return children

	if (showWhenHasPermission.length > 0 && showWhenHasPermission.some(hasPermission)) return children

	if (showWhenMissingPermission.length > 0 && !showWhenMissingPermission.some(hasPermission))
		return children

	// Showing NotAuthorized if not disabled and showWhenMissingPermission is not provided
	if (disableNotAuthorized || showWhenMissingPermission.length > 0) {
		return null
	}

	return <NotAuthorized />
}
